/**
 * 定时轮询。运维面板、队列统计这类没有推送通道的数据靠它刷新。
 *
 * 页面不可见时暂停：后台标签页每 15 秒打一次运维接口是纯粹的浪费，
 * 且浏览器本身会把后台定时器降频，得到的数据也不新鲜。
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export interface UsePollingResult<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
  /** 立即拉一次，不影响下一轮的节奏 */
  refresh: () => void;
}

export function usePolling<T>(
  fetcher: (signal: AbortSignal) => Promise<T>,
  intervalMs: number,
  enabled = true,
): UsePollingResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [tick, setTick] = useState(0);

  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const refresh = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    let timer: number | undefined;
    let ac: AbortController | null = null;

    const run = async () => {
      if (document.hidden) {
        schedule();
        return;
      }
      ac = new AbortController();
      setLoading(true);
      try {
        const result = await fetcherRef.current(ac.signal);
        if (!cancelled) {
          setData(result);
          setError(null);
        }
      } catch (e: unknown) {
        if (!cancelled && !(e instanceof DOMException && e.name === 'AbortError')) {
          setError(e instanceof Error ? e : new Error(String(e)));
        }
      } finally {
        if (!cancelled) setLoading(false);
        schedule();
      }
    };

    const schedule = () => {
      if (cancelled) return;
      timer = window.setTimeout(run, intervalMs);
    };

    void run();

    // 从后台切回来时立刻补一次，不用等下一个 interval
    const onVisible = () => {
      if (!document.hidden) refresh();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
      ac?.abort();
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [intervalMs, enabled, tick, refresh]);

  return { data, loading, error, refresh };
}
